import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../../config/config";
import CockpitShell from "../../components/CockpitShell";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import { Progress } from "../../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  BarChart3,
  CheckCircle2,
  Download,
  FlaskConical,
  MoreHorizontal,
  Power,
  RefreshCw,
  Trash2,
  TriangleAlert,
  Users,
} from "lucide-react";
import { buildAuthHeaders } from "../../utils/authSession";

function authHeaders() {
  return buildAuthHeaders();
}

const navItems = [
  { href: "/dashboard/admin", label: "Command", icon: "🧠" },
  { href: "/dashboard/admin/users", label: "Users", icon: "👥" },
  { href: "/dashboard/admin/wissenstest", label: "Wissenstest", icon: "🧪" },
  { href: "/dashboard/admin/wissenstest-management", label: "WT Mgmt", icon: "🗂️" },
  { href: "/dashboard/systemstatus", label: "System", icon: "📡" },
];

const emptyForm = {
  question: "",
  wt_category_id: "",
  time_limit_sec: 15,
  is_active: true,
  options: [
    { answer_text: "", is_correct: true },
    { answer_text: "", is_correct: false },
    { answer_text: "", is_correct: false },
  ],
};

function useDebounced(value, ms = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

function ToastNotice({ toast }) {
  if (!toast) return null;
  const isError = toast.kind === "error";
  const Icon = isError ? TriangleAlert : CheckCircle2;
  return (
    <div className="fixed right-5 top-5 z-[80] w-[360px] max-w-[calc(100vw-2rem)] rounded-2xl border border-white/15 bg-slate-950/95 p-4 shadow-2xl backdrop-blur">
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 rounded-full p-1 ${isError ? "bg-rose-500/20 text-rose-200" : "bg-emerald-500/20 text-emerald-200"}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-white">{toast.title}</p>
          {toast.description ? <p className="mt-1 text-sm text-slate-300">{toast.description}</p> : null}
        </div>
      </div>
    </div>
  );
}

function ProfessionPicker({
  search,
  onSearchChange,
  hits,
  onSelect,
  selectedItems,
  onRemove,
  placeholder,
}) {
  return (
    <div className="space-y-2">
      <Input
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={placeholder}
        className="border-white/20 bg-slate-900/50 text-white"
      />
      {hits.length > 0 ? (
        <div className="max-h-44 overflow-auto rounded-md border border-white/10 bg-slate-900/70 p-2">
          {hits.map((p) => (
            <button
              key={p.id || p.beruf_id}
              type="button"
              className="mb-1 block w-full rounded px-2 py-1 text-left text-sm text-slate-200 hover:bg-white/10"
              onClick={() => onSelect(p)}
            >
              {p.beruf}
            </button>
          ))}
        </div>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {selectedItems.map((p) => (
          <Badge key={p.beruf_id} className="bg-cyan-500/20 text-cyan-100">
            {p.beruf}
            <button type="button" className="ml-2 text-cyan-200" onClick={() => onRemove(p.beruf_id)}>x</button>
          </Badge>
        ))}
      </div>
    </div>
  );
}

export default function WissenstestManagement() {
  const navigate = useNavigate();
  const selectContentClass = "border-white/15 bg-slate-950/98 text-slate-100 shadow-2xl";

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search, 350);
  const [activeFilter, setActiveFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");

  const [categories, setCategories] = useState([]);
  const [categorySearch, setCategorySearch] = useState("");
  const categorySearchDebounced = useDebounced(categorySearch, 300);
  const [categoryActiveFilter, setCategoryActiveFilter] = useState("all");
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [deleteCategoryTarget, setDeleteCategoryTarget] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: "",
    description: "",
    berufsgruppe: "",
    is_active: true,
  });

  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const [professionSearch, setProfessionSearch] = useState("");
  const debouncedProfessionSearch = useDebounced(professionSearch, 300);
  const [professionHits, setProfessionHits] = useState([]);
  const [selectedProfessions, setSelectedProfessions] = useState([]);
  const [bulkProfessionSearch, setBulkProfessionSearch] = useState("");
  const debouncedBulkProfessionSearch = useDebounced(bulkProfessionSearch, 300);
  const [bulkProfessionHits, setBulkProfessionHits] = useState([]);
  const [bulkProfessions, setBulkProfessions] = useState([]);
  const [bulkProfessionMode, setBulkProfessionMode] = useState("add");
  const [bulkRemoveProfessionSearch, setBulkRemoveProfessionSearch] = useState("");
  const debouncedBulkRemoveProfessionSearch = useDebounced(bulkRemoveProfessionSearch, 300);
  const [bulkRemoveProfessionHits, setBulkRemoveProfessionHits] = useState([]);
  const [bulkRemoveProfessions, setBulkRemoveProfessions] = useState([]);

  const [statsOpen, setStatsOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [questionProfessionsMap, setQuestionProfessionsMap] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkCategoryId, setBulkCategoryId] = useState("");
  const [bulkTimerValue, setBulkTimerValue] = useState(15);
  const [bulkBusyLabel, setBulkBusyLabel] = useState("");
  const [selectionBusy, setSelectionBusy] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState(null);
  const [toast, setToast] = useState(null);

  const categoryMap = useMemo(() => {
    const out = new Map();
    categories.forEach((c) => out.set(String(c.id), c.name));
    return out;
  }, [categories]);

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const currentPageIds = useMemo(
    () => rows.map((row) => Number(row.id)).filter((id) => Number.isInteger(id) && id > 0),
    [rows]
  );
  const selectedCount = selectedIds.length;
  const pageSelectedCount = currentPageIds.filter((id) => selectedIdSet.has(id)).length;
  const currentPageSelectionState = currentPageIds.length === 0
    ? false
    : pageSelectedCount === currentPageIds.length
      ? true
      : pageSelectedCount > 0
        ? "indeterminate"
        : false;

  function categoryLabel(row) {
    const name = row?.category_name || categoryMap.get(String(row?.wt_category_id)) || "-";
    const group = String(row?.category_berufsgruppe || "").trim();
    return group ? `${name} / ${group}` : name;
  }

  function pushToast(kind, title, description = "") {
    setToast({ id: Date.now(), kind, title, description });
  }

  function resetSelection() {
    setSelectedIds([]);
  }

  function handleApiError(e, fallbackMessage = "Aktion fehlgeschlagen.", options = {}) {
    const status = e?.response?.status;
    const msg = e?.response?.data?.message || fallbackMessage;
    if (status === 401) {
      window.alert("Nicht angemeldet oder Session abgelaufen. Du wirst zum Login weitergeleitet.");
      navigate("/login");
      return;
    }
    if (status === 403) {
      window.alert("Keine Berechtigung fuer diese Aktion.");
      return;
    }
    if (options.useToast) {
      pushToast("error", "Aktion fehlgeschlagen", msg);
      return;
    }
    window.alert(msg);
  }

  async function loadCategories() {
    const res = await axios.get(`${config.API_BASE_URL}/api/admin/wtmgmt/categories`, {
      params: {
        page: 1,
        limit: 300,
        q: categorySearchDebounced || undefined,
        active: categoryActiveFilter === "all" ? undefined : categoryActiveFilter,
      },
      headers: authHeaders(),
    });
    setCategories(res.data?.rows || []);
  }

  async function loadQuestions(nextPage = page, nextLimit = limit) {
    const res = await axios.get(`${config.API_BASE_URL}/api/admin/wtmgmt/questions`, {
      params: questionParams(nextPage, nextLimit),
      headers: authHeaders(),
    });
    setRows(res.data?.rows || []);
    setTotal(Number(res.data?.total || 0));
    setPage(Number(res.data?.page || nextPage));
    setLimit(Number(res.data?.limit || nextLimit));
  }

  useEffect(() => {
    document.title = "Experts4Europe | Wissenstest Management";
    Promise.all([loadCategories(), loadQuestions(1, limit)]).catch(() => {});
  }, []);

  useEffect(() => {
    resetSelection();
    loadQuestions(1, limit).catch(() => {});
  }, [debouncedSearch, activeFilter, categoryFilter]);

  useEffect(() => {
    loadCategories().catch(() => {});
  }, [categorySearchDebounced, categoryActiveFilter]);

  useEffect(() => {
    if (debouncedProfessionSearch.trim().length < 2) {
      setProfessionHits([]);
      return;
    }
    axios
      .get(`${config.API_BASE_URL}/api/stb/berufe`, {
        params: { q: debouncedProfessionSearch, limit: 20 },
        headers: authHeaders(),
      })
      .then((res) => setProfessionHits(res.data?.rows || res.data || []))
      .catch(() => setProfessionHits([]));
  }, [debouncedProfessionSearch]);

  useEffect(() => {
    if (debouncedBulkProfessionSearch.trim().length < 2) {
      setBulkProfessionHits([]);
      return;
    }
    axios
      .get(`${config.API_BASE_URL}/api/stb/berufe`, {
        params: { q: debouncedBulkProfessionSearch, limit: 20 },
        headers: authHeaders(),
      })
      .then((res) => setBulkProfessionHits(res.data?.rows || res.data || []))
      .catch(() => setBulkProfessionHits([]));
  }, [debouncedBulkProfessionSearch]);

  useEffect(() => {
    if (debouncedBulkRemoveProfessionSearch.trim().length < 2) {
      setBulkRemoveProfessionHits([]);
      return;
    }
    axios
      .get(`${config.API_BASE_URL}/api/stb/berufe`, {
        params: { q: debouncedBulkRemoveProfessionSearch, limit: 20 },
        headers: authHeaders(),
      })
      .then((res) => setBulkRemoveProfessionHits(res.data?.rows || res.data || []))
      .catch(() => setBulkRemoveProfessionHits([]));
  }, [debouncedBulkRemoveProfessionSearch]);

  useEffect(() => {
    if (!toast) return undefined;
    const timeout = setTimeout(() => setToast(null), 3200);
    return () => clearTimeout(timeout);
  }, [toast]);

  function resetDialog() {
    setEditingId(null);
    setForm(emptyForm);
    setSelectedProfessions([]);
    setProfessionSearch("");
    setProfessionHits([]);
  }

  async function openCreateDialog() {
    resetDialog();
    setQuestionDialogOpen(true);
  }

  async function openEditDialog(id) {
    const res = await axios.get(`${config.API_BASE_URL}/api/admin/wtmgmt/questions/${id}`, {
      headers: authHeaders(),
    });
    const q = res.data || {};
    setEditingId(q.id);
    setForm({
      question: q.question || "",
      wt_category_id: q.wt_category_id ? String(q.wt_category_id) : "",
      time_limit_sec: Number(q.time_limit_sec || 15),
      is_active: Number(q.is_active || 0) === 1,
      options: Array.isArray(q.options) && q.options.length
        ? q.options.slice(0, 3).map((o) => ({ answer_text: o.answer_text || "", is_correct: Number(o.is_correct || 0) === 1 }))
        : emptyForm.options,
    });
    setSelectedProfessions((q.professions || []).map((p) => ({ beruf_id: Number(p.beruf_id), beruf: p.beruf })));
    setQuestionDialogOpen(true);
  }

  function toggleCorrect(idx) {
    setForm((prev) => {
      const next = { ...prev, options: [...prev.options] };
      next.options[idx] = { ...next.options[idx], is_correct: !next.options[idx].is_correct };
      return next;
    });
  }

  function updateOptionText(idx, value) {
    setForm((prev) => {
      const next = { ...prev, options: [...prev.options] };
      next.options[idx] = { ...next.options[idx], answer_text: value };
      return next;
    });
  }

  function addProfession(item) {
    const berufId = Number(item.id || item.beruf_id);
    if (!Number.isFinite(berufId) || berufId <= 0) return;
    setSelectedProfessions((prev) => {
      if (prev.some((p) => p.beruf_id === berufId)) return prev;
      return [...prev, { beruf_id: berufId, beruf: item.beruf }];
    });
  }

  function removeProfession(berufId) {
    setSelectedProfessions((prev) => prev.filter((p) => p.beruf_id !== berufId));
  }

  function addBulkProfession(item) {
    const berufId = Number(item.id || item.beruf_id);
    if (!Number.isFinite(berufId) || berufId <= 0) return;
    setBulkProfessions((prev) => {
      if (prev.some((p) => p.beruf_id === berufId)) return prev;
      return [...prev, { beruf_id: berufId, beruf: item.beruf }];
    });
  }

  function removeBulkProfession(berufId) {
    setBulkProfessions((prev) => prev.filter((p) => p.beruf_id !== berufId));
  }

  function addBulkRemoveProfession(item) {
    const berufId = Number(item.id || item.beruf_id);
    if (!Number.isFinite(berufId) || berufId <= 0) return;
    setBulkRemoveProfessions((prev) => {
      if (prev.some((p) => p.beruf_id === berufId)) return prev;
      return [...prev, { beruf_id: berufId, beruf: item.beruf }];
    });
  }

  function removeBulkRemoveProfession(berufId) {
    setBulkRemoveProfessions((prev) => prev.filter((p) => p.beruf_id !== berufId));
  }

  function toggleRowSelection(rowId, checked) {
    const id = Number(rowId);
    if (!Number.isInteger(id) || id <= 0) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return [...next];
    });
  }

  function toggleCurrentPageSelection(checked) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      currentPageIds.forEach((id) => {
        if (checked) next.add(id);
        else next.delete(id);
      });
      return [...next];
    });
  }

  function questionParams(nextPage = page, nextLimit = limit) {
    return {
      page: nextPage,
      limit: nextLimit,
      q: debouncedSearch || undefined,
      active: activeFilter === "all" ? undefined : activeFilter,
      category_id: categoryFilter === "all" ? undefined : categoryFilter,
    };
  }

  async function collectFilteredQuestionIds() {
    const maxBulkSelection = 500;
    if (Number(total || 0) > maxBulkSelection) {
      throw new Error(`Bitte Filter weiter einschränken. Maximal ${maxBulkSelection} Fragen können gleichzeitig verarbeitet werden.`);
    }
    const allIds = [];
    let nextPage = 1;
    let fetchedTotal = Number(total || 0);
    while (true) {
      const res = await axios.get(`${config.API_BASE_URL}/api/admin/wtmgmt/questions`, {
        params: questionParams(nextPage, 100),
        headers: authHeaders(),
      });
      const nextRows = res.data?.rows || [];
      fetchedTotal = Number(res.data?.total || fetchedTotal || 0);
      nextRows.forEach((row) => {
        const id = Number(row.id);
        if (Number.isInteger(id) && id > 0) allIds.push(id);
      });
      if (allIds.length >= fetchedTotal || nextRows.length === 0) break;
      nextPage += 1;
    }
    return [...new Set(allIds)];
  }

  async function selectAllFilteredQuestions() {
    setSelectionBusy(true);
    try {
      const ids = await collectFilteredQuestionIds();
      setSelectedIds(ids);
      pushToast("success", "Alle Treffer selektiert", `${ids.length} Fragen sind aktuell ausgewählt.`);
    } catch (e) {
      handleApiError(e, e?.message || "Treffer konnten nicht geladen werden.", { useToast: true });
    } finally {
      setSelectionBusy(false);
    }
  }

  async function runBulkRequest(label, request, successMessage) {
    setBulkBusyLabel(label);
    try {
      await request();
      pushToast("success", "Bulk-Aktion abgeschlossen", successMessage);
      resetSelection();
      await loadQuestions(page, limit);
      setQuestionProfessionsMap({});
      setBulkCategoryId("");
      setBulkTimerValue(15);
      setBulkProfessions([]);
      setBulkRemoveProfessions([]);
      setBulkProfessionSearch("");
      setBulkRemoveProfessionSearch("");
      setBulkProfessionHits([]);
      setBulkRemoveProfessionHits([]);
    } catch (e) {
      handleApiError(e, `${label} fehlgeschlagen.`, { useToast: true });
    } finally {
      setBulkBusyLabel("");
    }
  }

  function requestBulkConfirmation(config) {
    setBulkConfirm(config);
  }

  async function downloadCsv(scope) {
    const params = scope === "selected"
      ? { scope: "selected", ids: selectedIds.join(",") }
      : {
          scope: "filtered",
          q: debouncedSearch || undefined,
          category_id: categoryFilter === "all" ? undefined : categoryFilter,
          active: activeFilter === "all" ? undefined : activeFilter,
        };
    setBulkBusyLabel(scope === "selected" ? "CSV der Auswahl wird erstellt" : "CSV der Trefferliste wird erstellt");
    try {
      const res = await axios.get(`${config.API_BASE_URL}/api/admin/wtmgmt/questions/export`, {
        params,
        headers: authHeaders(),
        responseType: "blob",
      });
      const disposition = String(res.headers?.["content-disposition"] || "");
      const match = disposition.match(/filename="?([^"]+)"?/i);
      const filename = match?.[1] || `wt-questions-${scope}.csv`;
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      pushToast("success", "Export bereit", scope === "selected" ? "CSV der Auswahl wurde heruntergeladen." : "CSV der Trefferliste wurde heruntergeladen.");
    } catch (e) {
      handleApiError(e, "Export fehlgeschlagen.", { useToast: true });
    } finally {
      setBulkBusyLabel("");
    }
  }

  async function saveQuestion() {
    try {
      const hasCorrect = form.options.some((o) => o.is_correct);
      if (!form.question.trim() || !form.wt_category_id || !hasCorrect) {
        window.alert("Bitte Frage, Kategorie und mindestens eine richtige Antwort setzen.");
        return;
      }

      const payload = {
        question: form.question.trim(),
        wt_category_id: Number(form.wt_category_id),
        time_limit_sec: Math.max(5, Number(form.time_limit_sec || 15)),
        is_active: !!form.is_active,
        topic: "legacy",
        difficulty: "medium",
        type: form.options.filter((o) => o.is_correct).length > 1 ? "multi" : "single",
        explanation: null,
      };

      let questionId = editingId;
      if (editingId) {
        await axios.put(`${config.API_BASE_URL}/api/admin/wtmgmt/questions/${editingId}`, payload, { headers: authHeaders() });
      } else {
        const created = await axios.post(`${config.API_BASE_URL}/api/admin/wtmgmt/questions`, payload, { headers: authHeaders() });
        questionId = Number(created.data?.row?.id || created.data?.id);
      }

      const optionsPayload = {
        options: form.options.map((o, idx) => ({
          answer_text: String(o.answer_text || "").trim(),
          is_correct: !!o.is_correct,
          sort_order: idx + 1,
        })),
      };
      await axios.put(`${config.API_BASE_URL}/api/admin/wtmgmt/questions/${questionId}/options`, optionsPayload, {
        headers: authHeaders(),
      });

      await axios.put(
        `${config.API_BASE_URL}/api/admin/wtmgmt/questions/${questionId}/professions`,
        { beruf_ids: selectedProfessions.map((p) => p.beruf_id) },
        { headers: authHeaders() }
      );

      setQuestionDialogOpen(false);
      resetDialog();
      await loadQuestions(page, limit);
    } catch (e) {
      handleApiError(e, "Speichern fehlgeschlagen.");
    }
  }

  async function toggleQuestionActive(row) {
    await axios.patch(
      `${config.API_BASE_URL}/api/admin/wtmgmt/questions/${row.id}/active`,
      { active: Number(row.is_active || 0) === 0 },
      { headers: authHeaders() }
    );
    await loadQuestions(page, limit);
  }

  async function deleteQuestion(row) {
    await axios.delete(`${config.API_BASE_URL}/api/admin/wtmgmt/questions/${row.id}`, { headers: authHeaders() });
    await loadQuestions(page, limit);
  }

  async function ensureQuestionProfessions(questionId) {
    if (questionProfessionsMap[questionId]) return;
    try {
      const res = await axios.get(`${config.API_BASE_URL}/api/admin/wtmgmt/questions/${questionId}/professions`, {
        headers: authHeaders(),
      });
      const rowsData = res.data?.rows || [];
      setQuestionProfessionsMap((prev) => ({ ...prev, [questionId]: rowsData }));
    } catch (_e) {
      setQuestionProfessionsMap((prev) => ({ ...prev, [questionId]: [] }));
    }
  }

  async function openStats(row) {
    const res = await axios.get(`${config.API_BASE_URL}/api/admin/wtmgmt/questions/${row.id}/stats`, {
      headers: authHeaders(),
    });
    setStats(res.data || null);
    setStatsOpen(true);
  }

  async function runTest(row) {
    const res = await axios.post(`${config.API_BASE_URL}/api/admin/wtmgmt/questions/${row.id}/test-run`, {}, { headers: authHeaders() });
    const attemptId = Number(res.data?.attempt_id || 0);
    if (attemptId > 0) navigate(`/assessment/wt/run/${attemptId}`);
  }

  function resetCategoryDialog() {
    setEditingCategoryId(null);
    setCategoryForm({
      name: "",
      description: "",
      berufsgruppe: "",
      is_active: true,
    });
  }

  function openCategoryCreate() {
    resetCategoryDialog();
    setCategoryDialogOpen(true);
  }

  function openCategoryEdit(row) {
    setEditingCategoryId(row.id);
    setCategoryForm({
      name: row.name || "",
      description: row.description || "",
      berufsgruppe: row.berufsgruppe || "",
      is_active: Number(row.is_active || 0) === 1,
    });
    setCategoryDialogOpen(true);
  }

  async function saveCategory() {
    if (!categoryForm.name.trim()) {
      window.alert("Kategorie-Name ist erforderlich.");
      return;
    }
    const payload = {
      name: categoryForm.name.trim(),
      description: categoryForm.description || null,
      berufsgruppe: categoryForm.berufsgruppe || null,
      is_active: !!categoryForm.is_active,
    };
    if (editingCategoryId) {
      await axios.put(`${config.API_BASE_URL}/api/admin/wtmgmt/categories/${editingCategoryId}`, payload, { headers: authHeaders() });
    } else {
      await axios.post(`${config.API_BASE_URL}/api/admin/wtmgmt/categories`, payload, { headers: authHeaders() });
    }
    setCategoryDialogOpen(false);
    resetCategoryDialog();
    await loadCategories();
  }

  async function toggleCategoryActive(row) {
    await axios.patch(
      `${config.API_BASE_URL}/api/admin/wtmgmt/categories/${row.id}/active`,
      { active: Number(row.is_active || 0) === 0 },
      { headers: authHeaders() }
    );
    await loadCategories();
  }

  async function deleteCategory(row) {
    try {
      await axios.delete(`${config.API_BASE_URL}/api/admin/wtmgmt/categories/${row.id}`, { headers: authHeaders() });
      await loadCategories();
    } catch (e) {
      const msg = e?.response?.data?.message || "Kategorie konnte nicht geloescht werden.";
      window.alert(msg);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <CockpitShell
      title="Wissenstest Management"
      subtitle="Operatives Grid fuer Fragen, Kategorien und Zuordnungen"
      badge="Admin WT"
      navItems={navItems}
      actions={<Button className="bg-cyan-500 text-slate-950 hover:bg-cyan-300" onClick={openCreateDialog}>Neue Frage</Button>}
    >
      <Tabs defaultValue="questions" className="w-full">
        <TabsList className="bg-white/5">
          <TabsTrigger value="questions">Fragen</TabsTrigger>
          <TabsTrigger value="categories">Kategorien</TabsTrigger>
        </TabsList>

        <TabsContent value="questions" className="space-y-4">
          <Card className="card-e4e">
            <CardHeader>
              <CardTitle className="font-display text-white">Fragenliste</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2 md:grid-cols-4">
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Frage suchen"
                  className="border-white/20 bg-slate-950/40 text-white"
                />
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="border-white/20 bg-slate-950/40 text-white">
                    <SelectValue placeholder="Kategorie" />
                  </SelectTrigger>
                  <SelectContent className={selectContentClass}>
                    <SelectItem value="all">Alle Kategorien</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={activeFilter} onValueChange={setActiveFilter}>
                  <SelectTrigger className="border-white/20 bg-slate-950/40 text-white">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent className={selectContentClass}>
                    <SelectItem value="all">Alle Status</SelectItem>
                    <SelectItem value="1">Online</SelectItem>
                    <SelectItem value="0">Offline</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={String(limit)} onValueChange={(v) => { const n = Number(v); setLimit(n); loadQuestions(1, n).catch(() => {}); }}>
                  <SelectTrigger className="border-white/20 bg-slate-950/40 text-white">
                    <SelectValue placeholder="Pro Seite" />
                  </SelectTrigger>
                  <SelectContent className={selectContentClass}>
                    <SelectItem value="10">10 / Seite</SelectItem>
                    <SelectItem value="20">20 / Seite</SelectItem>
                    <SelectItem value="50">50 / Seite</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-white/20 bg-transparent text-slate-100"
                  onClick={() => {
                    setSearch("");
                    setCategoryFilter("all");
                    setActiveFilter("all");
                    resetSelection();
                  }}
                >
                  Filter zuruecksetzen
                </Button>
                {search ? (
                  <Badge className="cursor-pointer bg-cyan-500/20 text-cyan-100" onClick={() => setSearch("")}>
                    Suche: {search} x
                  </Badge>
                ) : null}
                {categoryFilter !== "all" ? (
                  <Badge className="cursor-pointer bg-cyan-500/20 text-cyan-100" onClick={() => setCategoryFilter("all")}>
                    Kategorie: {categoryMap.get(categoryFilter) || categoryFilter} x
                  </Badge>
                ) : null}
                {activeFilter !== "all" ? (
                  <Badge className="cursor-pointer bg-cyan-500/20 text-cyan-100" onClick={() => setActiveFilter("all")}>
                    Status: {activeFilter === "1" ? "Online" : "Offline"} x
                  </Badge>
                ) : null}
              </div>

              <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-white/10 bg-slate-950/60 px-4 py-3">
                <div className="text-sm text-slate-200">
                  <span className="font-semibold text-white">{selectedCount}</span> ausgewaehlt
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-white/20 bg-transparent text-slate-100"
                  disabled={selectedCount === 0}
                  onClick={resetSelection}
                >
                  Auswahl zuruecksetzen
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-white/20 bg-transparent text-slate-100"
                  disabled={selectionBusy || total === 0}
                  onClick={selectAllFilteredQuestions}
                >
                  {selectionBusy ? "Treffer werden geladen..." : `Alle Treffer auswaehlen (${total})`}
                </Button>
                <span className="text-xs text-slate-400">
                  Serverseitige Pagination bleibt aktiv. Bulk arbeitet auf Auswahl, nicht nur auf der Ansicht.
                </span>
              </div>

              {selectedCount > 0 ? (
                <div className="space-y-4 rounded-[28px] border border-cyan-400/25 bg-[radial-gradient(circle_at_top_left,_rgba(6,182,212,0.18),_transparent_45%),linear-gradient(145deg,rgba(15,23,42,0.96),rgba(2,6,23,0.98))] p-5 shadow-[0_24px_80px_rgba(2,6,23,0.45)]">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.25em] text-cyan-200/80">Bulk Operations</p>
                      <h3 className="mt-1 text-lg font-semibold text-white">{selectedCount} Fragen in Bearbeitung</h3>
                      <p className="mt-1 text-sm text-slate-300">Status, Kategorie, Berufe, Timer, Archivierung und Export in einem Durchlauf.</p>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-300">
                      <Users className="h-4 w-4 text-cyan-300" />
                      Auswahl bleibt ueber Seiten erhalten, bis du sie zuruecksetzt.
                    </div>
                  </div>

                  {bulkBusyLabel ? (
                    <div className="space-y-2 rounded-2xl border border-white/10 bg-slate-950/55 px-4 py-3">
                      <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] text-cyan-200/80">
                        <span>{bulkBusyLabel}</span>
                        <RefreshCw className="h-4 w-4 animate-spin" />
                      </div>
                      <Progress value={65} className="h-2 bg-white/10 [&>div]:bg-cyan-400" />
                    </div>
                  ) : null}

                  <div className="grid gap-4 xl:grid-cols-2">
                    <div className="space-y-3 rounded-2xl border border-white/10 bg-slate-950/55 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">Status</p>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          className="bg-emerald-500 text-slate-950 hover:bg-emerald-400"
                          disabled={!!bulkBusyLabel}
                          onClick={() => requestBulkConfirmation({
                            title: "Fragen online schalten?",
                            description: `${selectedCount} Fragen werden online geschaltet.`,
                            confirmLabel: "Online schalten",
                            confirmClassName: "bg-emerald-600 text-white hover:bg-emerald-500",
                            onConfirm: () => runBulkRequest(
                              "Status wird auf online gesetzt",
                              () => axios.post(
                                `${config.API_BASE_URL}/api/admin/wtmgmt/questions/bulk/status`,
                                { ids: selectedIds, is_active: 1 },
                                { headers: authHeaders() }
                              ),
                              `${selectedCount} Fragen sind jetzt online.`
                            ),
                          })}
                        >
                          Online schalten
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-amber-300/30 bg-amber-500/10 text-amber-100 hover:bg-amber-500/20"
                          disabled={!!bulkBusyLabel}
                          onClick={() => requestBulkConfirmation({
                            title: "Fragen offline schalten?",
                            description: `${selectedCount} Fragen werden offline geschaltet.`,
                            confirmLabel: "Offline schalten",
                            confirmClassName: "bg-amber-600 text-white hover:bg-amber-500",
                            onConfirm: () => runBulkRequest(
                              "Status wird auf offline gesetzt",
                              () => axios.post(
                                `${config.API_BASE_URL}/api/admin/wtmgmt/questions/bulk/status`,
                                { ids: selectedIds, is_active: 0 },
                                { headers: authHeaders() }
                              ),
                              `${selectedCount} Fragen sind jetzt offline.`
                            ),
                          })}
                        >
                          Offline schalten
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-3 rounded-2xl border border-white/10 bg-slate-950/55 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">Kategorie setzen</p>
                      <div className="flex flex-col gap-3 sm:flex-row">
                        <Select value={bulkCategoryId} onValueChange={setBulkCategoryId}>
                          <SelectTrigger className="border-white/20 bg-slate-900/60 text-white">
                            <SelectValue placeholder="Kategorie waehlen" />
                          </SelectTrigger>
                          <SelectContent className={selectContentClass}>
                            {categories.map((c) => (
                              <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          size="sm"
                          className="bg-cyan-500 text-slate-950 hover:bg-cyan-300"
                          disabled={!bulkCategoryId || !!bulkBusyLabel}
                          onClick={() => requestBulkConfirmation({
                            title: "Kategorie fuer Auswahl setzen?",
                            description: `Die Kategorie wird fuer ${selectedCount} Fragen auf "${categoryMap.get(bulkCategoryId) || bulkCategoryId}" gesetzt.`,
                            confirmLabel: "Kategorie anwenden",
                            confirmClassName: "bg-cyan-600 text-white hover:bg-cyan-500",
                            onConfirm: () => runBulkRequest(
                              "Kategorie wird gesetzt",
                              () => axios.post(
                                `${config.API_BASE_URL}/api/admin/wtmgmt/questions/bulk/category`,
                                { ids: selectedIds, category_id: Number(bulkCategoryId) },
                                { headers: authHeaders() }
                              ),
                              `Kategorie wurde fuer ${selectedCount} Fragen gesetzt.`
                            ),
                          })}
                        >
                          Apply
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-3 rounded-2xl border border-white/10 bg-slate-950/55 p-4">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">Berufe zuweisen</p>
                        <Select value={bulkProfessionMode} onValueChange={setBulkProfessionMode}>
                          <SelectTrigger className="h-9 w-[180px] border-white/20 bg-slate-900/60 text-white">
                            <SelectValue placeholder="Modus" />
                          </SelectTrigger>
                          <SelectContent className={selectContentClass}>
                            <SelectItem value="add">Add (zusaetzlich)</SelectItem>
                            <SelectItem value="replace">Replace (ersetzen)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <ProfessionPicker
                        search={bulkProfessionSearch}
                        onSearchChange={setBulkProfessionSearch}
                        hits={bulkProfessionHits}
                        onSelect={addBulkProfession}
                        selectedItems={bulkProfessions}
                        onRemove={removeBulkProfession}
                        placeholder="Berufe suchen und zur Bulk-Auswahl hinzufuegen"
                      />
                      <Button
                        size="sm"
                        className="bg-cyan-500 text-slate-950 hover:bg-cyan-300"
                        disabled={bulkProfessions.length === 0 || !!bulkBusyLabel}
                        onClick={() => requestBulkConfirmation({
                          title: bulkProfessionMode === "replace" ? "Berufe ersetzen?" : "Berufe hinzufuegen?",
                          description: bulkProfessionMode === "replace"
                            ? `${bulkProfessions.length} Berufe ersetzen die bisherigen Zuordnungen fuer ${selectedCount} Fragen.`
                            : `${bulkProfessions.length} Berufe werden zu ${selectedCount} Fragen hinzugefuegt.`,
                          confirmLabel: bulkProfessionMode === "replace" ? "Replace" : "Add",
                          confirmClassName: "bg-cyan-600 text-white hover:bg-cyan-500",
                          onConfirm: () => runBulkRequest(
                            bulkProfessionMode === "replace" ? "Berufe werden ersetzt" : "Berufe werden hinzugefuegt",
                            () => axios.post(
                              `${config.API_BASE_URL}/api/admin/wtmgmt/questions/bulk/professions`,
                              {
                                ids: selectedIds,
                                beruf_ids: bulkProfessions.map((item) => item.beruf_id),
                                mode: bulkProfessionMode,
                              },
                              { headers: authHeaders() }
                            ),
                            `Berufs-Zuordnungen wurden fuer ${selectedCount} Fragen aktualisiert.`
                          ),
                        })}
                      >
                        Apply
                      </Button>
                    </div>

                    <div className="space-y-3 rounded-2xl border border-white/10 bg-slate-950/55 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">Berufe entfernen</p>
                      <ProfessionPicker
                        search={bulkRemoveProfessionSearch}
                        onSearchChange={setBulkRemoveProfessionSearch}
                        hits={bulkRemoveProfessionHits}
                        onSelect={addBulkRemoveProfession}
                        selectedItems={bulkRemoveProfessions}
                        onRemove={removeBulkRemoveProfession}
                        placeholder="Berufe suchen, die aus der Auswahl entfernt werden sollen"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-rose-300/30 bg-rose-500/10 text-rose-100 hover:bg-rose-500/20"
                        disabled={bulkRemoveProfessions.length === 0 || !!bulkBusyLabel}
                        onClick={() => requestBulkConfirmation({
                          title: "Berufe aus Auswahl entfernen?",
                          description: `${bulkRemoveProfessions.length} Berufe werden bei ${selectedCount} Fragen entfernt.`,
                          confirmLabel: "Remove",
                          confirmClassName: "bg-rose-600 text-white hover:bg-rose-500",
                          onConfirm: () => runBulkRequest(
                            "Berufe werden entfernt",
                            () => axios.post(
                              `${config.API_BASE_URL}/api/admin/wtmgmt/questions/bulk/professions/remove`,
                              {
                                ids: selectedIds,
                                beruf_ids: bulkRemoveProfessions.map((item) => item.beruf_id),
                              },
                              { headers: authHeaders() }
                            ),
                            `Berufe wurden bei ${selectedCount} Fragen entfernt.`
                          ),
                        })}
                      >
                        Remove
                      </Button>
                    </div>

                    <div className="space-y-3 rounded-2xl border border-white/10 bg-slate-950/55 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">Sekunden setzen</p>
                      <div className="flex flex-col gap-3 sm:flex-row">
                        <Input
                          type="number"
                          min={5}
                          max={180}
                          value={bulkTimerValue}
                          onChange={(e) => setBulkTimerValue(Number(e.target.value || 0))}
                          className="border-white/20 bg-slate-900/60 text-white"
                        />
                        <Button
                          size="sm"
                          className="bg-cyan-500 text-slate-950 hover:bg-cyan-300"
                          disabled={!Number.isInteger(Number(bulkTimerValue)) || Number(bulkTimerValue) < 5 || Number(bulkTimerValue) > 180 || !!bulkBusyLabel}
                          onClick={() => requestBulkConfirmation({
                            title: "Timer fuer Auswahl setzen?",
                            description: `${selectedCount} Fragen erhalten ein Zeitlimit von ${bulkTimerValue} Sekunden.`,
                            confirmLabel: "Timer setzen",
                            confirmClassName: "bg-cyan-600 text-white hover:bg-cyan-500",
                            onConfirm: () => runBulkRequest(
                              "Timer wird gesetzt",
                              () => axios.post(
                                `${config.API_BASE_URL}/api/admin/wtmgmt/questions/bulk/timer`,
                                { ids: selectedIds, time_limit_sec: Number(bulkTimerValue) },
                                { headers: authHeaders() }
                              ),
                              `Timer wurde fuer ${selectedCount} Fragen auf ${bulkTimerValue} Sekunden gesetzt.`
                            ),
                          })}
                        >
                          Apply
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-3 rounded-2xl border border-white/10 bg-slate-950/55 p-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">Export & Archivierung</p>
                      <div className="flex flex-wrap gap-2">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-white/20 bg-transparent text-slate-100"
                              disabled={!!bulkBusyLabel}
                            >
                              <Download className="mr-2 h-4 w-4" />
                              Export CSV
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className={selectContentClass}>
                            <DropdownMenuItem onClick={() => downloadCsv("selected")}>
                              Export Auswahl
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => downloadCsv("filtered")}>
                              Export gefilterte Liste
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-rose-300/30 bg-rose-500/10 text-rose-100 hover:bg-rose-500/20"
                          disabled={!!bulkBusyLabel}
                          onClick={() => requestBulkConfirmation({
                            title: "Fragen archivieren?",
                            description: `${selectedCount} Fragen wirklich loeschen/archivieren? Die Bulk-Aktion setzt is_active auf 0.`,
                            confirmLabel: "Archivieren",
                            confirmClassName: "bg-rose-600 text-white hover:bg-rose-500",
                            onConfirm: () => runBulkRequest(
                              "Fragen werden archiviert",
                              () => axios.post(
                                `${config.API_BASE_URL}/api/admin/wtmgmt/questions/bulk/delete`,
                                { ids: selectedIds, mode: "soft" },
                                { headers: authHeaders() }
                              ),
                              `${selectedCount} Fragen wurden archiviert.`
                            ),
                          })}
                        >
                          Loeschen
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="overflow-x-auto rounded-xl border border-white/10 pb-4">
                <table className="min-w-[1240px] text-sm">
                  <thead className="bg-slate-900/80 text-left text-slate-300">
                    <tr>
                      <th className="px-3 py-2">
                        <Checkbox
                          checked={currentPageSelectionState}
                          onCheckedChange={(checked) => toggleCurrentPageSelection(!!checked)}
                          aria-label="Aktuelle Seite auswaehlen"
                        />
                      </th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Frage</th>
                      <th className="px-3 py-2">Kategorie</th>
                      <th className="px-3 py-2">Sek.</th>
                      <th className="px-3 py-2">Berufe</th>
                      <th className="sticky right-0 z-20 border-l border-white/10 bg-slate-900/95 px-3 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.id} className="border-t border-white/10 text-slate-200">
                        <td className="px-3 py-1.5">
                          <Checkbox
                            checked={selectedIdSet.has(Number(row.id))}
                            onCheckedChange={(checked) => toggleRowSelection(row.id, !!checked)}
                            aria-label={`Frage ${row.id} auswaehlen`}
                          />
                        </td>
                        <td className="px-3 py-1.5">
                          <span
                            title={Number(row.is_active || 0) === 1 ? "Online" : "Offline"}
                            className={`inline-block h-3 w-3 rounded-full ${Number(row.is_active || 0) === 1 ? "bg-emerald-400" : "bg-rose-400"}`}
                          />
                        </td>
                        <td className="min-w-[520px] max-w-[620px] px-3 py-1.5">
                          <div
                            title={row.question}
                            style={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {row.question}
                          </div>
                        </td>
                        <td className="min-w-[280px] max-w-[360px] px-3 py-1.5" title={categoryLabel(row)}>
                          <div className="truncate">{categoryLabel(row)}</div>
                        </td>
                        <td className="px-3 py-1.5">{row.time_limit_sec || 15}</td>
                        <td className="px-3 py-1.5">
                          {Number(row.profession_count || 0) > 0 ? (
                            <Popover onOpenChange={(open) => { if (open) ensureQuestionProfessions(row.id); }}>
                              <PopoverTrigger asChild>
                                <button
                                  type="button"
                                  className="inline-flex items-center gap-2 rounded border border-white/20 px-2 py-1 text-xs text-slate-100 hover:bg-white/10"
                                  title="Zugeordnete Berufe anzeigen"
                                >
                                  <Badge variant="outline" className="border-white/20 text-slate-100">{Number(row.profession_count || 0)}</Badge>
                                  <span>Berufe</span>
                                </button>
                              </PopoverTrigger>
                              <PopoverContent className="w-80">
                                <div className="space-y-2">
                                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-300">Zugeordnete Berufe</p>
                                  {(questionProfessionsMap[row.id] || []).slice(0, 10).map((p) => (
                                    <p key={`${row.id}-${p.beruf_id}`} className="text-sm text-slate-100">{p.beruf}</p>
                                  ))}
                                  {(questionProfessionsMap[row.id] || []).length > 10 ? (
                                    <p className="text-xs text-slate-400">...</p>
                                  ) : null}
                                </div>
                              </PopoverContent>
                            </Popover>
                          ) : (
                            <span className="inline-flex items-center gap-2" title="Keine Berufe zugeordnet">
                              <Badge variant="outline" className="border-white/20 text-slate-100">0</Badge>
                              <span className="text-xs text-slate-300">Berufe</span>
                            </span>
                          )}
                        </td>
                        <td className="sticky right-0 z-10 w-[148px] border-l border-white/10 bg-slate-950/95 px-3 py-1.5">
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" className="h-8 border-white/20 bg-transparent text-slate-100" onClick={() => openEditDialog(row.id)}>Edit</Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="icon" variant="outline" className="h-8 w-8 border-white/20 bg-transparent text-slate-100" title="Weitere Aktionen">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem title={Number(row.is_active || 0) === 1 ? "Frage offline schalten" : "Frage online schalten"} onClick={() => toggleQuestionActive(row)}>
                                  <Power className="mr-2 h-4 w-4" />
                                  {Number(row.is_active || 0) === 1 ? "Offline schalten" : "Online schalten"}
                                </DropdownMenuItem>
                                <DropdownMenuItem title="Statistik anzeigen" onClick={() => openStats(row)}>
                                  <BarChart3 className="mr-2 h-4 w-4" />
                                  Statistik
                                </DropdownMenuItem>
                                <DropdownMenuItem title="Testlauf starten" onClick={() => runTest(row)}>
                                  <FlaskConical className="mr-2 h-4 w-4" />
                                  Test
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-rose-300 focus:bg-rose-500/15 focus:text-rose-200"
                                  title="Frage loeschen"
                                  onClick={() => setDeleteTarget(row)}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {rows.length === 0 ? (
                      <tr>
                        <td className="px-3 py-6 text-center text-slate-400" colSpan={7}>Keine Fragen gefunden.</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between text-sm text-slate-300">
                <div>
                  {rows.length > 0 ? `${(page - 1) * limit + 1}-${(page - 1) * limit + rows.length} von ${total}` : `0 von ${total}`}
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" className="border-white/20 bg-transparent text-slate-100" disabled={page <= 1} onClick={() => loadQuestions(page - 1, limit)}>
                    Zurueck
                  </Button>
                  <span>Seite {page} / {totalPages}</span>
                  <Button size="sm" variant="outline" className="border-white/20 bg-transparent text-slate-100" disabled={page >= totalPages} onClick={() => loadQuestions(page + 1, limit)}>
                    Weiter
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="categories">
          <Card className="card-e4e">
            <CardHeader>
              <CardTitle className="font-display text-white">Kategorien</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-2 md:grid-cols-3">
                <Input
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                  placeholder="Kategorie suchen"
                  className="border-white/20 bg-slate-950/40 text-white"
                />
                <Select value={categoryActiveFilter} onValueChange={setCategoryActiveFilter}>
                  <SelectTrigger className="border-white/20 bg-slate-950/40 text-white">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent className={selectContentClass}>
                    <SelectItem value="all">Alle Status</SelectItem>
                    <SelectItem value="1">Online</SelectItem>
                    <SelectItem value="0">Offline</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                  <Button className="bg-cyan-500 text-slate-950 hover:bg-cyan-300" onClick={openCategoryCreate}>Neue Kategorie</Button>
                  <Button
                    variant="outline"
                    className="border-white/20 bg-transparent text-slate-100"
                    onClick={() => {
                      setCategorySearch("");
                      setCategoryActiveFilter("all");
                    }}
                  >
                    Filter reset
                  </Button>
                </div>
              </div>

              <div className="overflow-x-auto rounded-xl border border-white/10">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-900/80 text-left text-slate-300">
                    <tr>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Name</th>
                      <th className="px-3 py-2">Berufsgruppe</th>
                      <th className="px-3 py-2">Beschreibung</th>
                      <th className="px-3 py-2">Fragen</th>
                      <th className="px-3 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((c) => (
                      <tr key={c.id} className="border-t border-white/10 text-slate-200">
                        <td className="px-3 py-1.5">
                          <span
                            title={Number(c.is_active || 0) === 1 ? "Online" : "Offline"}
                            className={`inline-block h-3 w-3 rounded-full ${Number(c.is_active || 0) === 1 ? "bg-emerald-400" : "bg-rose-400"}`}
                          />
                        </td>
                        <td className="px-3 py-1.5">{c.name}</td>
                        <td className="px-3 py-1.5">{c.berufsgruppe || "-"}</td>
                        <td className="max-w-[420px] px-3 py-1.5" title={c.description || ""}>
                          <div
                            style={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {c.description || "-"}
                          </div>
                        </td>
                        <td className="px-3 py-1.5">{Number(c.question_count || 0)}</td>
                        <td className="px-3 py-1.5">
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" className="h-8 border-white/20 bg-transparent text-slate-100" onClick={() => openCategoryEdit(c)}>Edit</Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="icon" variant="outline" className="h-8 w-8 border-white/20 bg-transparent text-slate-100" title="Weitere Aktionen">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => toggleCategoryActive(c)}>
                                  <Power className="mr-2 h-4 w-4" />
                                  {Number(c.is_active || 0) === 1 ? "Offline schalten" : "Online schalten"}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-rose-300 focus:bg-rose-500/15 focus:text-rose-200"
                                  onClick={() => setDeleteCategoryTarget(c)}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {categories.length === 0 ? (
                      <tr>
                        <td className="px-3 py-6 text-center text-slate-400" colSpan={6}>Keine Kategorien gefunden.</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={questionDialogOpen} onOpenChange={setQuestionDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto border-white/15 bg-slate-950/95 text-slate-100">
          <DialogHeader>
            <DialogTitle>{editingId ? "Frage bearbeiten" : "Neue Frage"}</DialogTitle>
            <DialogDescription className="text-slate-300">Kategorie, Antworten, Timer und Berufe verwalten.</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2 md:col-span-2">
              <label className="text-xs uppercase tracking-wide text-slate-400">Frage</label>
              <Input value={form.question} onChange={(e) => setForm((p) => ({ ...p, question: e.target.value }))} className="border-white/20 bg-slate-900/50 text-white" />
            </div>

            <div className="space-y-2">
              <label className="text-xs uppercase tracking-wide text-slate-400">Kategorie</label>
              <Select value={form.wt_category_id || ""} onValueChange={(v) => setForm((p) => ({ ...p, wt_category_id: v }))}>
                <SelectTrigger className="border-white/20 bg-slate-900/50 text-white">
                  <SelectValue placeholder="Kategorie waehlen" />
                </SelectTrigger>
                <SelectContent className={selectContentClass}>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-xs uppercase tracking-wide text-slate-400">Sekunden</label>
              <Input type="number" min={5} value={form.time_limit_sec} onChange={(e) => setForm((p) => ({ ...p, time_limit_sec: Number(e.target.value || 15) }))} className="border-white/20 bg-slate-900/50 text-white" />
            </div>

            <div className="space-y-2 md:col-span-2">
              <label className="text-xs uppercase tracking-wide text-slate-400">Berufe zuordnen</label>
              <ProfessionPicker
                search={professionSearch}
                onSearchChange={setProfessionSearch}
                hits={professionHits}
                onSelect={addProfession}
                selectedItems={selectedProfessions}
                onRemove={removeProfession}
                placeholder="Beruf suchen (mindestens 2 Zeichen)"
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <label className="text-xs uppercase tracking-wide text-slate-400">Antworten</label>
              {form.options.map((opt, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_auto] items-center gap-3">
                  <Input value={opt.answer_text} onChange={(e) => updateOptionText(idx, e.target.value)} placeholder={`Antwort ${idx + 1}`} className="border-white/20 bg-slate-900/50 text-white" />
                  <label className="flex items-center gap-2 text-sm text-slate-200">
                    <Checkbox checked={opt.is_correct} onCheckedChange={() => toggleCorrect(idx)} />
                    Richtig
                  </label>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" className="border-white/20 bg-transparent text-slate-100" onClick={() => setQuestionDialogOpen(false)}>Cancel</Button>
            <Button className="bg-cyan-500 text-slate-950 hover:bg-cyan-300" onClick={saveQuestion}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={statsOpen} onOpenChange={setStatsOpen}>
        <DialogContent className="border-white/15 bg-slate-950/95 text-slate-100">
          <DialogHeader>
            <DialogTitle>Fragen-Statistik</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm text-slate-200">
            <div>Attempts: {stats?.attempts ?? 0}</div>
            <div>Antworten gesamt: {stats?.answers_total ?? 0}</div>
            <div>Richtige Antworten: {stats?.answers_correct ?? 0}</div>
            <div>Correct Rate: {stats?.correct_rate ?? 0}%</div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent className="border-white/15 bg-slate-950/95 text-slate-100">
          <DialogHeader>
            <DialogTitle>{editingCategoryId ? "Kategorie bearbeiten" : "Neue Kategorie"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-wide text-slate-400">Name</label>
              <Input value={categoryForm.name} onChange={(e) => setCategoryForm((p) => ({ ...p, name: e.target.value }))} className="border-white/20 bg-slate-900/50 text-white" />
            </div>
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-wide text-slate-400">Beschreibung</label>
              <Input value={categoryForm.description} onChange={(e) => setCategoryForm((p) => ({ ...p, description: e.target.value }))} className="border-white/20 bg-slate-900/50 text-white" />
            </div>
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-wide text-slate-400">Berufsgruppe (optional)</label>
              <Input value={categoryForm.berufsgruppe} onChange={(e) => setCategoryForm((p) => ({ ...p, berufsgruppe: e.target.value }))} className="border-white/20 bg-slate-900/50 text-white" />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-200">
              <Checkbox checked={categoryForm.is_active} onCheckedChange={(v) => setCategoryForm((p) => ({ ...p, is_active: !!v }))} />
              Online
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-white/20 bg-transparent text-slate-100" onClick={() => setCategoryDialogOpen(false)}>Cancel</Button>
            <Button className="bg-cyan-500 text-slate-950 hover:bg-cyan-300" onClick={saveCategory}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent className="border-white/15 bg-slate-950/95 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle>Frage loeschen?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-300">
              Diese Aktion entfernt die Frage inklusive Optionen und Mapping dauerhaft.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/20 bg-transparent text-slate-100 hover:bg-white/10">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 text-white hover:bg-rose-500"
              onClick={async () => {
                if (!deleteTarget) return;
                await deleteQuestion(deleteTarget);
                setDeleteTarget(null);
              }}
            >
              Loeschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteCategoryTarget} onOpenChange={(open) => { if (!open) setDeleteCategoryTarget(null); }}>
        <AlertDialogContent className="border-white/15 bg-slate-950/95 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle>Kategorie loeschen?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-300">
              Die Kategorie wird dauerhaft geloescht. Falls Fragen zugeordnet sind, blockiert das Backend den Vorgang.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/20 bg-transparent text-slate-100 hover:bg-white/10">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 text-white hover:bg-rose-500"
              onClick={async () => {
                if (!deleteCategoryTarget) return;
                await deleteCategory(deleteCategoryTarget);
                setDeleteCategoryTarget(null);
              }}
            >
              Loeschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!bulkConfirm} onOpenChange={(open) => { if (!open) setBulkConfirm(null); }}>
        <AlertDialogContent className="border-white/15 bg-slate-950/95 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle>{bulkConfirm?.title || "Bulk-Aktion bestaetigen"}</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-300">
              {bulkConfirm?.description || "Die ausgewaehlten Fragen werden aktualisiert."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/20 bg-transparent text-slate-100 hover:bg-white/10" disabled={!!bulkBusyLabel}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              className={bulkConfirm?.confirmClassName || "bg-cyan-600 text-white hover:bg-cyan-500"}
              disabled={!!bulkBusyLabel}
              onClick={async (event) => {
                event.preventDefault();
                if (!bulkConfirm?.onConfirm) {
                  setBulkConfirm(null);
                  return;
                }
                await bulkConfirm.onConfirm();
                setBulkConfirm(null);
              }}
            >
              {bulkConfirm?.confirmLabel || "Ausfuehren"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ToastNotice toast={toast} />
    </CockpitShell>
  );
}
